import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, ArrowRight, ArrowLeft, Check, Info, Loader2, FileCheck2, Package, Truck, Scissors, X } from "lucide-react";
import { toast } from "sonner";
import { RevealText, Reveal } from "@/lib/motion";
import { TID, api, photos, errMsg} from "@/lib/config";
import { Link, useNavigate } from "react-router-dom";
import DxfPreview from "@/components/DxfPreview";

const STEPS = [
  { n: 1, key: "specs", label: "Specifiche" },
  { n: 2, key: "file", label: "File & spedizione" },
  { n: 3, key: "estimate", label: "Preventivo stimato" },
  { n: 4, key: "contact", label: "Contatti & invio" },
];

const MATERIALS = [
  { key: "acciaio", name: "Acciaio Inox", max: 10 },
  { key: "ferro", name: "Ferro (Acc. carbonio)", max: 15 },
];

export default function LaserPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [dwgFile, setDwgFile] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const dwgRef = useRef(null);
  const [f, setF] = useState({
    material_key: "acciaio",
    thickness_mm: 3,
    width_mm: 500,
    height_mm: 300,
    quantity: 10,
    estimated_cut_length_m: "",
    postal_code: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
  });

  const material = useMemo(() => MATERIALS.find((m) => m.key === f.material_key), [f.material_key]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) });

  const validStep1 = () => {
    if (!f.material_key || !f.thickness_mm || !f.width_mm || !f.height_mm || !f.quantity) return "Compila tutti i campi tecnici";
    if (Number(f.thickness_mm) > material.max) return `Spessore massimo per ${material.name}: ${material.max}mm`;
    if (Number(f.width_mm) > 3000 || Number(f.height_mm) > 3000) return "Dimensione massima 3000mm";
    if (Number(f.quantity) < 1) return "Quantità minima 1";
    return null;
  };

  const goStep2 = () => {
    const err = validStep1();
    if (err) return toast.error(err);
    setStep(2);
  };

  const goEstimate = async () => {
    setEstimating(true);
    try {
      const payload = {
        material_key: f.material_key,
        thickness_mm: Number(f.thickness_mm),
        width_mm: Number(f.width_mm),
        height_mm: Number(f.height_mm),
        quantity: Number(f.quantity),
        postal_code: f.postal_code || null,
        estimated_cut_length_m: f.estimated_cut_length_m ? Number(f.estimated_cut_length_m) : null,
      };
      const r = await api.post("/laser/estimate", payload);
      setEstimate(r.data);
      setStep(3);
    } catch (e) {
      toast.error(errMsg(e, "Errore nel calcolo del preventivo"));
    } finally {
      setEstimating(false);
    }
  };

  const submit = async () => {
    if (!f.name || !f.email) return toast.error("Nome ed email sono obbligatori");
    if (!privacy) return toast.error("Accetta la privacy policy per procedere");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({
        name: f.name, email: f.email, phone: f.phone, company: f.company,
        material_key: f.material_key, thickness_mm: f.thickness_mm,
        width_mm: f.width_mm, height_mm: f.height_mm, quantity: f.quantity,
        postal_code: f.postal_code, notes: f.notes,
      }).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (f.estimated_cut_length_m) fd.append("estimated_cut_length_m", f.estimated_cut_length_m);
      if (dwgFile) fd.append("dwg_file", dwgFile);

      const r = await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Richiesta inviata!");
      nav(`/preventivo/${r.data.quote_id}`);
    } catch (e) {
      toast.error(errMsg(e, "Errore invio richiesta"));
    } finally { setSubmitting(false); }
  };

  const onDwg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error("File troppo grande (max 30MB)");
    setDwgFile(file);
  };

  return (
    <div className="pt-24 pb-24">
      {/* Hero */}
      <section className="relative pt-16 pb-24 border-b border-[#141414] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={photos.laser[0]} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 to-[#050505]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="font-mono-tech text-white/60 hover:text-[#ff4b00]"><ArrowLeft size={12} className="inline mr-1" />Home</Link>
              <span className="font-mono-tech text-white/30">/</span>
              <span className="font-mono-tech text-[#ff4b00]">Taglio laser</span>
            </div>
          </Reveal>
          <RevealText text="Preventivo Taglio Laser." className="font-display text-white text-[11vw] md:text-[6.5vw] leading-[0.9]" />
          <Reveal delay={0.3}>
            <p className="text-white/70 max-w-2xl mt-6 md:text-lg">
              4 passaggi, meno di 2 minuti. Il preventivo automatico viene sempre revisionato da un
              tecnico Gefion prima del pagamento — nessuna sorpresa.
            </p>
          </Reveal>

          {/* Steps indicator */}
          <div className="mt-14 flex items-center gap-3 flex-wrap">
            {STEPS.map((s) => (
              <div key={s.n} className="flex items-center gap-3">
                <span className={`step-num ${step === s.n ? "active" : step > s.n ? "done" : ""}`}>
                  {step > s.n ? <Check size={14} /> : s.n}
                </span>
                <span className="font-mono-tech text-[10px] text-white/60 mr-2">{s.label}</span>
                {s.n < STEPS.length && <span className="w-8 h-px bg-[#222]" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wizard body */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="1" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <div className="mb-10">
                  <div className="num-chip mb-4"><span className="dot" /> Step 01 · Specifiche tecniche</div>
                  <div className="font-display text-4xl md:text-6xl leading-[0.95]">Cosa vuoi tagliare?</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                  <div>
                    <label className="field-label">Materiale</label>
                    <select data-testid={TID.laserMaterial} className="field-boxed mt-3" value={f.material_key} onChange={set("material_key")}>
                      {MATERIALS.map((m) => <option key={m.key} value={m.key}>{m.name} · max {m.max}mm</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Spessore (mm)</label>
                    <input data-testid={TID.laserThickness} type="number" min={0.5} step={0.5} max={material.max}
                      className="field-boxed mt-3" value={f.thickness_mm} onChange={setNum("thickness_mm")} />
                    <div className="font-mono-tech text-[10px] text-white/40 mt-2">Massimo {material.max} mm per {material.name}</div>
                  </div>
                  <div>
                    <label className="field-label">Larghezza pezzo (mm)</label>
                    <input data-testid={TID.laserWidth} type="number" min={1} max={3000} className="field-boxed mt-3" value={f.width_mm} onChange={setNum("width_mm")} />
                  </div>
                  <div>
                    <label className="field-label">Altezza pezzo (mm)</label>
                    <input data-testid={TID.laserHeight} type="number" min={1} max={3000} className="field-boxed mt-3" value={f.height_mm} onChange={setNum("height_mm")} />
                  </div>
                  <div>
                    <label className="field-label">Quantità</label>
                    <input data-testid={TID.laserQuantity} type="number" min={1} max={1000} className="field-boxed mt-3" value={f.quantity} onChange={setNum("quantity")} />
                  </div>
                  <div>
                    <label className="field-label">Lunghezza taglio stimata (m) — opzionale</label>
                    <input data-testid={TID.laserCutLength} type="number" min={0} step={0.1} className="field-boxed mt-3" value={f.estimated_cut_length_m} onChange={setNum("estimated_cut_length_m")} />
                    <div className="font-mono-tech text-[10px] text-white/40 mt-2">Se vuoto, stimiamo il perimetro dal bounding box.</div>
                  </div>
                </div>
                <div className="mt-12 flex items-center justify-between">
                  <div className="font-mono-tech text-[10px] text-white/40 flex items-center gap-2"><Info size={12} /> Il calcolo è indicativo, verrà validato da un tecnico.</div>
                  <button data-testid={TID.laserStep1Next} onClick={goStep2} className="btn-primary">Avanti <ArrowRight size={14} /></button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="2" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <div className="mb-10">
                  <div className="num-chip mb-4"><span className="dot" /> Step 02 · DWG & spedizione</div>
                  <div className="font-display text-4xl md:text-6xl leading-[0.95]">Carica il disegno.</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label className="field-label">Il tuo file (DWG · DXF · PDF · ZIP) — opzionale</label>
                    <button
                      type="button"
                      onClick={() => dwgRef.current?.click()}
                      className="mt-3 w-full aspect-[4/3] border border-dashed border-[#333] hover:border-[#ff4b00] transition-colors flex flex-col items-center justify-center gap-3 text-white/60 hover:text-white"
                    >
                      {dwgFile ? (
                        <>
                          <FileCheck2 size={40} className="text-[#22c55e]" />
                          <div className="font-mono-tech text-[11px]">{dwgFile.name}</div>
                          <div className="font-mono-tech text-[10px] text-white/40">{(dwgFile.size / 1024 / 1024).toFixed(2)} MB · Sostituisci</div>
                        </>
                      ) : (
                        <>
                          <Upload size={40} className="text-[#ff4b00]" />
                          <div className="font-mono-tech text-[11px]">Clicca o trascina qui</div>
                          <div className="font-mono-tech text-[10px] text-white/40">Max 30 MB</div>
                        </>
                      )}
                    </button>
                    {dwgFile && (
                      <button type="button" onClick={() => setDwgFile(null)} className="mt-3 font-mono-tech text-[10px] text-white/40 hover:text-white flex items-center gap-2">
                        <X size={12} /> rimuovi file
                      </button>
                    )}
                    <input ref={dwgRef} type="file" hidden data-testid={TID.laserDwg} accept=".dwg,.dxf,.pdf,.zip" onChange={onDwg} />
                    <div className="mt-4 font-mono-tech text-[10px] text-white/40">
                      Nota: puoi anche procedere senza file. Un tecnico ti contatterà per riceverlo.
                    </div>
                    <DxfPreview file={dwgFile} />
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="field-label">CAP di consegna</label>
                      <input data-testid={TID.laserPostal} className="field-boxed mt-3" placeholder="es. 20100" value={f.postal_code} onChange={set("postal_code")} maxLength={5} />
                      <div className="font-mono-tech text-[10px] text-white/40 mt-2">Serve per calcolare la spedizione (Italia).</div>
                    </div>
                    <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
                      <div className="font-mono-tech text-white/50 mb-4">Recap tecnico</div>
                      <div className="grid grid-cols-2 gap-y-3 font-mono-tech text-white/80 text-[11px]">
                        <div className="text-white/40">Materiale</div><div>{material.name}</div>
                        <div className="text-white/40">Spessore</div><div>{f.thickness_mm} mm</div>
                        <div className="text-white/40">Dimensioni</div><div>{f.width_mm} × {f.height_mm} mm</div>
                        <div className="text-white/40">Quantità</div><div>{f.quantity} pz</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-12 flex items-center justify-between">
                  <button onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft size={14} /> Indietro</button>
                  <button data-testid={TID.laserStep2Next} onClick={goEstimate} className="btn-primary" disabled={estimating}>
                    {estimating ? <><Loader2 size={14} className="animate-spin" /> Calcolo...</> : <>Calcola preventivo <ArrowRight size={14} /></>}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && estimate && (
              <motion.div key="3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <div className="mb-10">
                  <div className="num-chip mb-4"><span className="dot" /> Step 03 · Preventivo stimato</div>
                  <div className="font-display text-4xl md:text-6xl leading-[0.95]">Ecco la tua stima.</div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 border border-[#1a1a1a] p-8">
                    <div className="font-mono-tech text-white/50 mb-6">Dettaglio</div>
                    <table className="w-full font-mono-tech text-[12px]">
                      <tbody>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">Materiale ({material.name}) — {estimate.total_weight_kg} kg</td><td className="py-3 text-right text-white">€ {estimate.material_cost.toFixed(2)}</td></tr>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">Taglio ({estimate.cut_length_m} m)</td><td className="py-3 text-right text-white">€ {estimate.cut_cost.toFixed(2)}</td></tr>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">Setup macchina</td><td className="py-3 text-right text-white">€ {estimate.setup_fee.toFixed(2)}</td></tr>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">Subtotale (netto IVA)</td><td className="py-3 text-right text-white/90">€ {estimate.subtotal_no_vat.toFixed(2)}</td></tr>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">IVA 22%</td><td className="py-3 text-right text-white/80">€ {(estimate.total_with_vat - estimate.subtotal_no_vat).toFixed(2)}</td></tr>
                        <tr className="border-b border-[#151515]"><td className="py-3 text-white/50">Spedizione ({estimate.shipping.zone}, {estimate.shipping.weight_kg} kg)</td><td className="py-3 text-right text-white/90">€ {estimate.shipping.cost.toFixed(2)}</td></tr>
                        <tr><td className="pt-6 font-display text-2xl text-white">Totale stimato</td><td className="pt-6 text-right font-display text-3xl text-[#ff4b00]">€ {estimate.grand_total.toFixed(2)}</td></tr>
                      </tbody>
                    </table>
                    <div className="mt-8 flex items-start gap-3 border border-[#1a1a1a] bg-[#0a0a0a] p-4">
                      <Info size={16} className="text-[#ff4b00] shrink-0 mt-0.5" />
                      <div className="text-[13px] text-white/70">
                        Questa è una <strong className="text-white">stima automatica</strong>. Un tecnico Gefion revisionerà
                        il tuo disegno DWG e ti invierà il preventivo definitivo per l'approvazione e il pagamento.
                      </div>
                    </div>
                  </div>
                  <div className="border border-[#1a1a1a] p-8 bg-[#0a0a0a]">
                    <div className="font-mono-tech text-white/50 mb-4">Riepilogo pezzo</div>
                    <div className="space-y-3 font-mono-tech text-[11px]">
                      <div className="flex justify-between"><span className="text-white/40">Materiale</span><span>{material.name}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Spessore</span><span>{f.thickness_mm} mm</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Dimensioni</span><span>{f.width_mm}×{f.height_mm} mm</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Quantità</span><span>{f.quantity} pz</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Peso totale</span><span>{estimate.total_weight_kg} kg</span></div>
                      <div className="flex justify-between"><span className="text-white/40">CAP</span><span>{f.postal_code || "—"}</span></div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-[#1a1a1a] flex items-center gap-3 text-white/60 text-[11px] font-mono-tech">
                      <Package size={12} className="text-[#ff4b00]" /> Prod. 5-8 gg lavorativi
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-white/60 text-[11px] font-mono-tech">
                      <Truck size={12} className="text-[#ff4b00]" /> Spedizione 2-4 gg
                    </div>
                  </div>
                </div>
                <div className="mt-12 flex items-center justify-between">
                  <button onClick={() => setStep(2)} className="btn-ghost"><ArrowLeft size={14} /> Indietro</button>
                  <button data-testid={TID.laserStep3Next} onClick={() => setStep(4)} className="btn-primary">Procedi ai contatti <ArrowRight size={14} /></button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <div className="mb-10">
                  <div className="num-chip mb-4"><span className="dot" /> Step 04 · Contatti</div>
                  <div className="font-display text-4xl md:text-6xl leading-[0.95]">Ultimo passaggio.</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                  <div>
                    <label className="field-label">Nome e cognome *</label>
                    <input data-testid={TID.laserCustName} className="field-boxed mt-3" value={f.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input data-testid={TID.laserCustEmail} type="email" className="field-boxed mt-3" value={f.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className="field-label">Telefono</label>
                    <input data-testid={TID.laserCustPhone} className="field-boxed mt-3" value={f.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <label className="field-label">Azienda (opzionale)</label>
                    <input data-testid={TID.laserCustCompany} className="field-boxed mt-3" value={f.company} onChange={set("company")} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label">Note per il tecnico (tolleranze, finitura, urgenze...)</label>
                    <textarea data-testid={TID.laserCustNotes} rows={4} className="field-boxed mt-3 resize-none" value={f.notes} onChange={set("notes")} />
                  </div>
                  <label className="md:col-span-2 flex items-start gap-3 text-white/70 text-sm cursor-pointer pt-2">
                    <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="laser-privacy" className="mt-1 accent-[#ff4b00]" />
                    <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>. Autorizzo il trattamento dei miei dati e del file DWG per l'esecuzione dell'ordine. *</span>
                  </label>
                </div>
                <div className="mt-12 flex items-center justify-between">
                  <button onClick={() => setStep(3)} className="btn-ghost"><ArrowLeft size={14} /> Indietro</button>
                  <button data-testid={TID.laserStep4Submit} onClick={submit} disabled={submitting} className="btn-primary">
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> Invio...</> : <>Invia richiesta <Scissors size={14} /></>}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
